import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../ui/Button/Button';
import Dialog from '../../../ui/Dialog/Dialog';
import Icon from '../../../ui/Icon';
import { ICONS } from '../../../ui/icon-map';
import { confirmDialog } from '../../../ui/feedback';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ProgressBar from '../../../ui/Progress/ProgressBar';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../../ui/Table/Table';
import { CellId, CellText, CellPlate, CellMoney } from '../../../ui/DataTable/cells';
import Card from '../../../ui/Card';
import { finesService, fmtAED, errorMessage } from '../../../services/finesService';
import { followJob } from '../../../services/followJob';
import { Pill } from './ui';
import { useRowLinks, stickyTop } from './jobLinks';

// Remove the black points service charge lines Ejar holds on fines that do not owe one (owner, 6 Oct 2026: only a
// "Non-Payable Fines" fine owes it; "ازاى اشيل السيرفيس شارج من ال 58 مرة واحده"). The admin's alone, kept for
// emergencies. Each line is cancelled the way Ejar's Traffic Violations screen does it (it turns "Not Active", with no
// way back from Ejar's screen), under the admin's own Ejar login, and read back from Ejar afterwards.

const TONE = { removed: 'green', already: 'indigo', skipped: 'gray', failed: 'red', queued: 'gray' };
const LABEL = { removed: 'Removed', already: 'Already not active', skipped: 'Skipped', failed: 'Failed', queued: 'Waiting' };

const JobView = ({ job, onCancel, onBack, FineLink, ContractLink }) => {
    const rows = Object.values(job.results || {});
    const s = job.summary || {};
    const running = job.status === 'running';
    const state = running ? 'running' : job.status === 'done' ? 'done' : 'stopped';
    return (
        <div>
            <div className={`${stickyTop} mb-1`}>
            <div className="mb-3">
                <ProgressBar done={job.done} total={job.total} unit="lines" state={state}
                             label={`Removing on Ejar as ${job.ejarUser}`} note={`${job.stage || ''}${job.error ? ` · ${job.error}` : ''}`} />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3 text-sm">
                <div className="flex flex-wrap gap-2">
                    {!running && ['removed', 'already', 'skipped', 'failed'].filter((k) => s[k]).map((k) => <Pill key={k} tone={TONE[k]}>{LABEL[k]}: {s[k]}</Pill>)}
                </div>
                <div className="flex gap-2">
                    {running && <Button size="sm" variant="danger" leadingIcon={<Icon glyph={ICONS.close} size="sm" />} onClick={onCancel}>Stop</Button>}
                    {!running && <Button size="sm" leadingIcon={<Icon glyph={ICONS.arrowLeft} size="sm" />} onClick={onBack}>Back to the list</Button>}
                </div>
            </div>
            </div>
            <TableContainer maxHeight={520}>
                <Table sticky>
                    <TableHead><TableRow><TableHeader>#</TableHeader><TableHeader>Line on Ejar</TableHeader><TableHeader>Fine</TableHeader><TableHeader>Contract</TableHeader><TableHeader>Amount</TableHeader><TableHeader>Result</TableHeader><TableHeader>Detail</TableHeader></TableRow></TableHead>
                    <TableBody>
                        {rows.map((r, i) => (
                            <TableRow key={r.ticket}>
                                <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                <TableCell><CellId>{r.line}</CellId></TableCell><TableCell><FineLink ticket={r.ticket} /></TableCell>
                                <TableCell><ContractLink contract={r.contract} customer={r.customer} plate={r.plate} /></TableCell>
                                <TableCell><CellMoney value={Number(r.amount || 0)} currency="AED" /></TableCell>
                                <TableCell><Pill tone={TONE[r.status] || 'gray'}>{LABEL[r.status] || r.status}</Pill></TableCell>
                                <TableCell><CellText>{r.detail}</CellText></TableCell>
                            </TableRow>))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default function ServiceChargeRemoveModal({ onClose, onDone }) {
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [sel, setSel] = useState(new Set());
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const changed = useRef(false);
    const { FineLink, ContractLink, modals } = useRowLinks();

    const load = async () => {
        setError(null); setPreview(null);
        try {
            const p = await finesService.serviceChargeCancel({ commit: false });
            setPreview(p);
            setSel(new Set(p.rows.map((r) => r.ticket)));
        } catch (e) { setError(errorMessage(e)); }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = preview?.rows || [];
    const toggle = (t) => setSel((s) => { const n = new Set(s); if (n.has(t)) n.delete(t); else n.add(t); return n; });
    const allChecked = rows.length > 0 && sel.size === rows.length;
    const toggleAll = () => setSel(allChecked ? new Set() : new Set(rows.map((r) => r.ticket)));
    const total = rows.filter((r) => sel.has(r.ticket)).reduce((s, r) => s + (r.amount || 0), 0);

    const start = async () => {
        const chosen = [...sel];
        if (!chosen.length) return;
        if (!(await confirmDialog({
            title: 'Remove the service charges on Ejar?',
            message: `Cancel ${chosen.length} service charge line(s) (${fmtAED(total)}) on Ejar as ${preview.ejarUser}? Ejar keeps them as "Not Active" - its screen has no way to bring them back.`,
            confirmLabel: 'Remove',
            danger: true,
        }))) return;
        setBusy(true); setError(null);
        try {
            const started = await finesService.serviceChargeCancel({ commit: true, tickets: chosen });
            changed.current = true;
            await followJob(() => finesService.serviceChargeCancelJob(started.jobId), {
                onUpdate: (j) => { setError(null); setJob(j); },
                onRetry: (e) => setError(`Lost contact with the engine (${errorMessage(e)}) - still following the job, retrying…`),
            });
        } catch (e) { setError(errorMessage(e)); }
        setBusy(false);
    };
    const cancel = async () => { if (job?.jobId) { try { await finesService.serviceChargeCancelStop(job.jobId); } catch (e) { setError(errorMessage(e)); } } };
    const close = () => { if (busy) return; if (changed.current) onDone?.(); onClose(); };

    return (
        <Dialog open onClose={close} size="xl" title="Service charges not owed on Ejar" closeOnOverlay={!busy}>
            {error && <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {!preview && !error && !job && <LoadingSpinner message="Reading the fines…" />}
            {preview && !job && (
                <>
                    <div className="text-sm text-gray-600 mb-3">
                        {preview.mustConfigure
                            ? <span className="text-red-700 font-semibold">Save your own Ejar login first (The Engine → Ejar account): every change on Ejar is made under the user who makes it.</span>
                            : <>Only a fine in RTA&apos;s <b>Non-Payable Fines</b> tab owes the service charge. Lines are cancelled on Ejar as <b>{preview.ejarUser}</b>, each read on Ejar before and after. Nothing is sent until you confirm.</>}
                    </div>
                    <Card
                        className="mb-4"
                        title="Service charge lines to remove"
                        subtitle="Each line is checked on Ejar first: still active, on the fine's own contract, and a service charge - anything else is left alone."
                        actions={(
                            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                                <span>{sel.size} / {rows.length} selected · {fmtAED(total)} · {preview.contracts || 0} contract(s)</span>
                                <Button variant="danger" size="sm" disabled={preview.mustConfigure || busy || sel.size === 0} onClick={start}
                                        leadingIcon={<Icon glyph={ICONS.close} size="sm" />}>
                                    Remove on Ejar
                                </Button>
                            </div>
                        )}
                    >
                        {rows.length === 0 ? <div className="text-sm text-gray-500">Nothing to remove - no service charge line on Ejar sits on a fine that does not owe one.</div> : (
                            <TableContainer maxHeight={380}>
                                <Table sticky>
                                    <TableHead><TableRow>
                                        <TableHeader className="w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label={allChecked ? 'Clear selection' : 'Select all lines'} /></TableHeader>
                                        <TableHeader>#</TableHeader><TableHeader>Line on Ejar</TableHeader><TableHeader>Amount</TableHeader><TableHeader>Fine</TableHeader><TableHeader>RTA tab</TableHeader>
                                        <TableHeader>Contract</TableHeader><TableHeader>Customer</TableHeader><TableHeader>Plate</TableHeader><TableHeader>Fine date</TableHeader>
                                    </TableRow></TableHead>
                                    <TableBody>{rows.map((r, i) => (
                                        <TableRow key={r.ticket}>
                                            <TableCell><input type="checkbox" checked={sel.has(r.ticket)} onChange={() => toggle(r.ticket)} aria-label={`Select line ${r.line}`} /></TableCell>
                                            <TableCell><span className="ds-cell-num">{i + 1}</span></TableCell>
                                            <TableCell><CellId>{r.line}</CellId></TableCell><TableCell><CellMoney value={Number(r.amount || 0)} currency="AED" /></TableCell>
                                            <TableCell><FineLink ticket={r.ticket} /></TableCell><TableCell><CellText>{r.tab}</CellText></TableCell>
                                            <TableCell><ContractLink contract={r.contract} customer={r.customer} plate={r.plate} /></TableCell><TableCell><CellText>{r.customer}</CellText></TableCell>
                                            <TableCell><CellPlate>{r.plate}</CellPlate></TableCell><TableCell><span className="ds-cell-date">{r.date}</span></TableCell>
                                        </TableRow>))}</TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>
                    {preview.left?.length > 0 && (
                        <details className="text-sm mt-1"><summary className="cursor-pointer text-gray-600">{preview.left.length} line(s) left out</summary>
                            <ul className="mt-1 ml-4 list-disc text-gray-600">{preview.left.map((x) => <li key={x.ticket}><span className="font-mono">{x.ticket}</span>: {x.why}</li>)}</ul></details>
                    )}
                </>
            )}
            {job && <JobView job={job} onCancel={cancel} onBack={() => { setJob(null); load(); }} FineLink={FineLink} ContractLink={ContractLink} />}
            {modals}
        </Dialog>
    );
}
